/**
 * InvestorPreviewBanner — the bar an Owner sees while viewing the portal as an
 * investor ("view as investor"), with the X that returns them to admin.
 *
 * It also answers the question that motivates the preview: can this investor
 * actually see their investment, their documents and the update timeline? The
 * counts are what the portal will render for them, and the warnings are the
 * gaps the portal itself cannot show — above all a holding that never got the
 * investor's userId denormalized onto it, which the cap table displays and
 * their dashboard silently omits.
 */
import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Eye, X } from 'lucide-react';
import { useInvestorPreview } from '../../contexts/InvestorPreviewContext';
import { useInvestorPortalPreviewData } from '../../hooks/investor-relations/useInvestorPortalPreview';

export function InvestorPreviewBanner() {
  const { isPreviewing, stopPreview } = useInvestorPreview();
  const { data, isLoading, error } = useInvestorPortalPreviewData();
  const navigate = useNavigate();

  const handleExit = useCallback(() => {
    stopPreview();
    navigate('/investors/admin');
  }, [stopPreview, navigate]);

  if (!isPreviewing) return null;

  const investorName = data?.investor?.name ?? 'investor';
  const warnings = data?.warnings ?? [];

  return (
    <div className="bg-amber-50 border-b border-amber-300 print:hidden">
      <div className="px-4 sm:px-6 py-3">
        <div className="flex items-start gap-3">
          <Eye className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" aria-hidden="true" />

          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-amber-900">
              {isLoading
                ? 'Loading the investor view...'
                : `You are viewing the portal as ${investorName}`}
              <span className="ml-2 font-medium text-amber-700">
                Read-only preview
              </span>
            </p>

            {error && (
              <p className="mt-1 text-sm text-red-700">
                Could not load this investor&apos;s portal view. Exit the preview and try again.
              </p>
            )}

            {data && (
              <p className="mt-1 text-sm font-medium text-amber-800">
                They can see{' '}
                <strong>{data.holdings.length}</strong> holding
                {data.holdings.length === 1 ? '' : 's'},{' '}
                <strong>{data.documents.length}</strong> document
                {data.documents.length === 1 ? '' : 's'},{' '}
                <strong>{data.updates.length}</strong> update
                {data.updates.length === 1 ? '' : 's'} in their timeline, and{' '}
                <strong>{data.consents.length}</strong> consent
                {data.consents.length === 1 ? '' : 's'}.
              </p>
            )}

            {warnings.length > 0 && (
              <ul className="mt-2 space-y-1">
                {warnings.map((warning) => (
                  <li
                    key={warning.code}
                    className="flex items-start gap-2 text-sm text-amber-900"
                  >
                    <AlertTriangle
                      className="w-4 h-4 shrink-0 mt-0.5 text-amber-600"
                      aria-hidden="true"
                    />
                    <span>{warning.message}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <button
            type="button"
            onClick={handleExit}
            className="shrink-0 inline-flex items-center gap-1.5 rounded-xl px-3 py-2
                       text-sm font-bold text-amber-900 ring-1 ring-amber-400
                       hover:bg-amber-100 transition-colors min-h-[44px]"
            aria-label="Exit preview and return to the admin view"
          >
            <X className="w-4 h-4" aria-hidden="true" />
            Exit preview
          </button>
        </div>
      </div>
    </div>
  );
}
